import format from "date-fns/format"
import { HourReportDayAuditViewModel } from "../../../hour-reports/api/hour-report-day-audit"
import { makeAuditCheckboxSettings } from "./audit-checkbox-settings"
import { TreeGridApprovedHour, TreeGridApprovedHourDateGroup } from "./types"
import { makeActionButtonHtml, makeDateKey, makeStatusTip } from "./utils"

export const makeTreeGridApprovedHourDateGroup = ({
  date,
  dateFormat,
  dayAudit,
  reports,
  selectedMemberForDayAudit,
  enableAddingReports = Boolean(selectedMemberForDayAudit),
  translations,
}: MakeTreeGridApprovedHourDateGroupProps): TreeGridApprovedHourDateGroup => {
  const formattedDate = format(new Date(date), dateFormat)
  const readyForCheck = dayAudit?.isReadyForCheck || dayAudit?.isReadyForApproval || dayAudit?.isApproved ? 1 : 0
  const readyForApproval = dayAudit?.isReadyForApproval || dayAudit?.isApproved ? 1 : 0
  const approved = dayAudit?.isApproved ? 1 : 0

  return {
    id: makeDateKey(date), // This value must be in format "yyyy-MM-dd" to match the date format used in the dayAudit payload.
    Def: "DateGroup",
    canManageAsHourReportAdmin: dayAudit?.canManageAsHourReportAdmin ?? false,
    Expanded: 1,
    actions: enableAddingReports ? makeAddActionHtml(translations.add) : "",
    status: "open",
    reporterUserId: "",
    reporterUserFullName: "",
    date: formattedDate,
    projectOrTask: "",
    projectOrTaskSearchValue: "",
    projectOrTaskExportValue: "",
    description: "",
    worker: "",
    started: "",
    finished: "",
    hours: "",
    hoursClass: "reported-hours-total-value",
    hoursFormula: "Grids.FormatTotalApprovedHours(sum('durationInDecimalHours', '4'))",
    readyForCheckTip: makeStatusTip({
      action: translations.readyForCheck,
      person: dayAudit?.submittedBy,
      date: dayAudit?.submittedAt,
      dateFormat,
      formatMessage: translations.statusTip,
    }),
    readyForApprovalTip: makeStatusTip({
      action: translations.checked,
      person: dayAudit?.checkedBy,
      date: dayAudit?.checkedAt,
      dateFormat,
      formatMessage: translations.statusTip,
    }),
    approvedTip: makeStatusTip({
      action: translations.approved,
      person: dayAudit?.approvedBy,
      date: dayAudit?.approvedAt,
      dateFormat,
      formatMessage: translations.statusTip,
    }),
    durationInDecimalHours: 0,
    Items: reports,
    ...makeAuditCheckboxSettings({
      isMemberContext: Boolean(selectedMemberForDayAudit),
      readyForCheck,
      readyForCheckEnabled: dayAudit?.isReadyForCheckEnabled,
      readyForApproval,
      readyForApprovalEnabled: dayAudit?.isReadyForApprovalEnabled,
      approved,
      approvedEnabled: dayAudit?.isApprovedEnabled,
    }),
  }
}

const makeAddActionHtml = (label: string) => {
  return `
    <span class="approved-hours-actions">
      ${makeActionButtonHtml({
        action: "add",
        label,
        icon: "add",
      })}
    </span>
  `
}

type MakeTreeGridApprovedHourDateGroupProps = {
  date: Date
  dateFormat: string
  reports: TreeGridApprovedHour[]
  dayAudit: HourReportDayAuditViewModel | undefined
  selectedMemberForDayAudit?: string
  enableAddingReports?: boolean
  translations: ApprovedHourDateGroupTranslations
}

export type ApprovedHourDateGroupTranslations = {
  add: string
  readyForCheck: string
  checked: string
  approved: string
  statusTip: (values: { action: string; person: string; date: string }) => string
}
